import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, CircularProgress } from '@mui/material';
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import api, {
  XSRF_COOKIE_NAME,
  setUnauthorizedHandler,
} from '../api';

const PUBLIC_ROUTES = ['/login', '/initial-password'];

const isPublicRoutePathname = (pathname) => PUBLIC_ROUTES.includes(pathname);

const CSRF_REFRESH_ENDPOINT = '/api/auth/csrf';
const AUTH_ME_ENDPOINT = '/api/auth/me';
const AUTH_LOGIN_ENDPOINT = '/api/auth/login';
const AUTH_LOGOUT_ENDPOINT = '/api/auth/logout';
const CSRF_RETRY_DELAYS_MS = [500, 1000, 2000, 3000, 5000];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const hasXsrfCookie = () => {
  if (typeof document === 'undefined') {
    return false;
  }

  return document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .some((cookie) => cookie.startsWith(`${XSRF_COOKIE_NAME}=`));
};

const defaultAuthValue = {
  user: null,
  isAuthenticated: false,
  loading: false,
  authenticating: false,
  error: null,
  login: async () => {
    throw new Error('AuthProvider is not mounted');
  },
  logout: async () => {
    throw new Error('AuthProvider is not mounted');
  },
  refreshUser: async () => null,
  clearError: () => {},
  apiClient: api,
};

const AuthContext = createContext(defaultAuthValue);

const normalizeErrorMessage = (error, fallback) => {
  if (!error) {
    return fallback;
  }

  const status = error?.response?.status;
  if (status === 423) {
    return 'Пароль пользователя ещё не установлен. Используйте ссылку для первичной установки пароля.';
  }

  const responseData = error?.response?.data;
  if (typeof responseData === 'string') {
    return responseData;
  }

  if (responseData && typeof responseData === 'object') {
    if (typeof responseData.message === 'string') {
      return responseData.message;
    }
    if (typeof responseData.error === 'string') {
      return responseData.error;
    }
  }

  if (typeof error.message === 'string' && error.message.trim().length > 0) {
    return error.message;
  }

  return fallback;
};

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [authenticating, setAuthenticating] = useState(false);
  const [error, setError] = useState(null);
  const unauthorizedLockRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const ensureCsrfToken = useCallback(async ({ silent = false, force = false } = {}) => {
    if (!force && hasXsrfCookie()) {
      return;
    }

    let lastError = null;
    for (let attempt = 0; attempt < CSRF_RETRY_DELAYS_MS.length; attempt += 1) {
      try {
        await api.get(CSRF_REFRESH_ENDPOINT, { withCredentials: true });
        return;
      } catch (csrfError) {
        lastError = csrfError;
        const status = csrfError?.response?.status;
        const isNetworkError = !status;
        const shouldRetry = isNetworkError || status >= 500;
        if (!shouldRetry || attempt === CSRF_RETRY_DELAYS_MS.length - 1) {
          break;
        }
        await sleep(CSRF_RETRY_DELAYS_MS[attempt]);
      }
    }

    if (!silent && process.env.NODE_ENV !== 'production') {
      console.warn('Не удалось обновить CSRF токен', lastError);
    }
    throw lastError;
  }, []);

  const executeWithCsrfRetry = useCallback(
    async (operation, retryAllowed = true, { retryOnForbidden = false } = {}) => {
      try {
        return await operation();
      } catch (err) {
        const status = err?.response?.status;
        const shouldRetry =
          retryAllowed &&
          (status === 419 || (retryOnForbidden && status === 403));
        if (shouldRetry) {
          await ensureCsrfToken({ force: true });
          return executeWithCsrfRetry(operation, false, { retryOnForbidden });
        }
        throw err;
      }
    },
    [ensureCsrfToken],
  );

  const handleUnauthorized = useCallback((reason) => {
    if (!mountedRef.current) return;

    const status = reason?.response?.status ?? reason?.status ?? null;
    const possibleUrl =
      reason?.config?.url ??
      reason?.response?.config?.url ??
      reason?.request?.responseURL ??
      '';
    const requestUrl = typeof possibleUrl === 'string' ? possibleUrl : '';
    const isAuthMeRequest = requestUrl.includes(AUTH_ME_ENDPOINT);
    const shouldRedirect =
      status === 401 ||
      status === 419 ||
      status === 440 ||
      (status === 403 && (isAuthMeRequest || requestUrl.length === 0));

    console.log('[auth] handleUnauthorized fired', { status, requestUrl });

    if (!shouldRedirect) {
      return;
    }

    if (isPublicRoutePathname(location.pathname)) {
      console.log('[auth] handleUnauthorized: skip redirect on public route', location.pathname);
      return;
    }

    if (unauthorizedLockRef.current) return;
    unauthorizedLockRef.current = true;

    setUser(null);
    setAuthenticating(false);
    setInitializing(false);
    setError(null);

    const redirectPath = '/login';
    const doRedirect = () => {
      try {
        if (location.pathname !== redirectPath) {
          console.log('[auth] navigating to', redirectPath);
          navigate(redirectPath, { replace: true, state: { from: location, reason: status } });
          // Фоллбек, если navigate не сменил URL за тик
          setTimeout(() => {
            if (window.location.pathname !== redirectPath) {
              console.log('[auth] hard redirect fallback');
              window.location.assign(redirectPath);
            }
          }, 0);
        } else {
          console.log('[auth] already on /login');
        }
      } finally {
        setTimeout(() => { unauthorizedLockRef.current = false; }, 0);
      }
    };

    // делаем редирект сразу
    doRedirect();
  }, [location, navigate]);

  const fetchUser = useCallback(
    async ({ silent = false, redirectOnNull = false } = {}) => {
      try {
        console.log('[auth] fetchUser: start (silent=%s, redirectOnNull=%s)', silent, redirectOnNull);
        const response = await executeWithCsrfRetry(() => api.get(AUTH_ME_ENDPOINT, { withCredentials: true }));
        console.log('[auth] fetchUser: /me response', response?.status);

        const data = response?.data?.user ?? null;

        if (mountedRef.current) {
          setUser(data);
          if (!silent) setError(null);
        }

        if (redirectOnNull && !data) {
          console.log('[auth] fetchUser: user is null -> handleUnauthorized');
          handleUnauthorized({ response: { status: 401 } });
        }
        return data;
      } catch (err) {
        const status = err?.response?.status;
        console.log('[auth] fetchUser: caught', status);
        if (status === 401 || status === 403) {
          if (mountedRef.current) {
            setUser(null);
            if (!silent) setError(null);
          }
          const reason = (err && typeof err === 'object') ? err : { response: { status } };
          handleUnauthorized(reason);
          return null;
        }
        if (mountedRef.current && !silent) {
          setError(normalizeErrorMessage(err, 'Не удалось получить данные пользователя.'));
        }
        throw err;
      } finally {
        console.log('[auth] fetchUser: finish');
      }
    },
    [executeWithCsrfRetry, handleUnauthorized],
  );

  const clearError = useCallback(() => {
    if (mountedRef.current) {
      setError(null);
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(handleUnauthorized);
    return () => {
      setUnauthorizedHandler(null);
    };
  }, [handleUnauthorized]);

  useEffect(() => {
    let active = true;
    const init = async () => {
      try {
        await ensureCsrfToken({ silent: true });
        const onPublicRoute = isPublicRoutePathname(location.pathname);
        await fetchUser({ silent: true, redirectOnNull: !onPublicRoute });
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Не удалось восстановить сессию', err);
        }
      } finally {
        if (active && mountedRef.current) setInitializing(false);
      }
    };
    init();
    return () => {
      active = false;
    };
  }, [ensureCsrfToken, fetchUser, location.pathname]);

  const login = useCallback(
    async (credentials = {}) => {
      setAuthenticating(true);
      setError(null);
      try {
        await ensureCsrfToken({ force: true });
        await executeWithCsrfRetry(() => api.post(AUTH_LOGIN_ENDPOINT, credentials, {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
        }), true, { retryOnForbidden: true });
        const profile = await fetchUser({ silent: true });
        if (!profile) {
          throw new Error('Пользователь не найден после авторизации');
        }
        setError(null);
        return profile;
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401) {
          setError('Неверное имя пользователя или пароль.');
        } else if (status === 403) {
          setError(normalizeErrorMessage(err, 'Доступ запрещён. Проверьте учётные данные и повторите вход.'));
        } else {
          setError(normalizeErrorMessage(err, 'Не удалось выполнить вход.'));
        }
        throw err;
      } finally {
        if (mountedRef.current) {
          setAuthenticating(false);
        }
      }
    },
    [ensureCsrfToken, executeWithCsrfRetry, fetchUser],
  );

  const logout = useCallback(async () => {
    try {
      await executeWithCsrfRetry(() => api.post(AUTH_LOGOUT_ENDPOINT, null, { withCredentials: true }));
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Ошибка при попытке выхода', err);
      }
    } finally {
      if (mountedRef.current) {
        setUser(null);
        setError(null);
        setAuthenticating(false);
        setInitializing(false);
      }
      navigate('/login', { replace: true });
    }
  }, [executeWithCsrfRetry, navigate]);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      loading: initializing,
      authenticating,
      error,
      login,
      logout,
      refreshUser: fetchUser,
      clearError,
      apiClient: api,
    }),
    [user, initializing, authenticating, error, login, logout, fetchUser, clearError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function RequireAuth({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children ?? <Outlet />;
}
